<template>
  <v-dialog v-model="dialog" persistent scrollable max-width="680" :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card>
      <v-form class="fdlg__form" @submit.prevent="submit">
        <div class="fdlg__head">
          <div>
            <h2 class="fdlg__title">Cine aprobă demersurile</h2>
            <p class="fdlg__sub">{{ dept ? dept.name : "" }}</p>
          </div>
          <v-btn icon aria-label="Închide" @click="close">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text ref="body" class="fdlg__body">
          <div v-if="dept" class="dh__now" :class="`dh__now--${nowTone}`">
            <v-icon class="dh__now-icon" aria-hidden="true">{{ nowIcon }}</v-icon>
            <div>
              <p class="dh__now-title">{{ nowTitle }}</p>
              <p class="dh__now-text">{{ nowText }}</p>
            </div>
          </div>

          <section class="form-section">
            <div class="form-section__label"><v-icon small>mdi-account-tie-outline</v-icon>Șef propriu</div>
            <v-autocomplete
              v-model="selected"
              :items="candidates"
              :loading="loadingCandidates"
              :filter="matchPerson"
              item-text="name"
              item-value="id"
              label="Șefii acestui departament"
              placeholder="Niciunul: aprobă Șeful de deasupra"
              persistent-placeholder
              prepend-inner-icon="mdi-account-search-outline"
              multiple
              chips
              small-chips
              deletable-chips
              outlined
              dense
              :menu-props="{ contentClass: 'menu-wrap', maxHeight: 360 }"
              no-data-text="Niciun utilizator cu rolul Șef găsit"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ roleLabel(item.role) }}<template v-if="item.heads.length"> · Șef: {{ item.heads.join(", ") }}</template>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
            <p class="form-section__note dh__note">
              <v-icon small>mdi-arrow-up-bold-outline</v-icon>
              <span>{{ emptyText }}</span>
            </p>
            <p class="form-section__note dh__note">
              <v-icon small>mdi-account-cog-outline</v-icon>
              <span>
                În listă sunt utilizatorii activi cu rolul Șef de departament sau Șef DTI (rolul se schimbă în
                Utilizatori). Cine e adăugat devine și membru al departamentului; cine e scos rămâne membru doar dacă
                acesta e departamentul lui principal.
              </span>
            </p>
          </section>
        </v-card-text>

        <div class="fdlg__foot">
          <v-spacer></v-spacer>
          <v-btn text large @click="close">Anulare</v-btn>
          <v-btn class="btn-green cfg-btn" large type="submit" :loading="saving">
            <v-icon left>mdi-content-save-outline</v-icon>Salvează
          </v-btn>
        </div>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import * as helper from "@/helper/helper";
import { roleLabel } from "@/config/roles";
import * as api from "./api";
import { plain } from "./format";

const names = (heads) => heads.map((h) => h.name).join(", ");

/**
 * The Șefs ticked on one department. Without any, its requests go to the Șef of the nearest department above
 * (UserDepartmentService.approvers on the server). Emits submit({ dept, user_ids }); the tab saves and closes.
 */
export default {
  name: "DepartmentHeadsDialog",
  props: {
    // id -> department (with approvers / own_heads from GET /config/departments)
    byId: { type: Map, required: true },
    saving: { type: Boolean, default: false },
  },
  data() {
    return {
      dialog: false,
      dept: null,
      selected: [],
      candidates: [],
      loadingCandidates: false,
    };
  },
  computed: {
    approvers() {
      return (this.dept && this.dept.approvers) || { heads: [], from_id: null };
    },
    inherited() {
      return this.approvers.from_id !== null && this.approvers.from_id !== this.dept.id;
    },
    fromName() {
      const from = this.byId.get(this.approvers.from_id);
      return from ? from.name : "";
    },
    nowTone() {
      if (!this.approvers.heads.length) return "none";
      return this.inherited ? "inherited" : "own";
    },
    nowIcon() {
      return { none: "mdi-alert-outline", inherited: "mdi-arrow-up-bold-circle-outline", own: "mdi-account-check-outline" }[this.nowTone];
    },
    nowTitle() {
      if (!this.approvers.heads.length) return "Acum nu aprobă nimeni demersurile acestui departament";
      return `Acum aprobă: ${names(this.approvers.heads)}`;
    },
    nowText() {
      if (!this.approvers.heads.length) {
        return "Nici el, nici departamentele de deasupra lui nu au un Șef activ. Alegeți un Șef mai jos.";
      }
      if (this.inherited) {
        return `Departamentul nu are Șef propriu, așa că demersurile urcă la Șeful departamentului „${this.fromName}”.`;
      }
      return "Șefii acestui departament.";
    },
    // what happens with nobody ticked here: the approvers of the parent
    emptyText() {
      const parent = this.dept && this.byId.get(this.dept.parent_id);
      const up = parent && parent.approvers;
      if (!up || !up.heads.length) {
        return "Fără Șef propriu, demersurile urcă la Șeful departamentului de deasupra; acum nu există unul, deci nu le-ar aproba nimeni.";
      }
      const from = this.byId.get(up.from_id);
      return `Fără Șef propriu, demersurile urcă la ${names(up.heads)} (Șeful „${from ? from.name : parent.name}”). Un Șef propriu aprobă în locul lui.`;
    },
  },
  methods: {
    roleLabel,
    async open(dept) {
      this.dept = dept;
      this.selected = (dept.own_heads || []).map((h) => h.id);
      this.dialog = true;
      helper.scrollToTop(this);
      this.loadingCandidates = true;
      try {
        const list = await api.getHeadCandidates();
        // a Șef ticked here who lost the role stays visible, to be taken off
        const missing = (dept.own_heads || []).filter((h) => !list.some((c) => c.id === h.id));
        this.candidates = [...list, ...missing.map((h) => ({ ...h, role: "", heads: [] }))];
      } catch (error) {
        this.$emit("error", "Eroare la încărcarea șefilor");
      } finally {
        this.loadingCandidates = false;
      }
    },
    close() {
      this.dialog = false;
    },
    matchPerson(item, query) {
      const q = plain(query);
      return plain(item.name).includes(q) || item.heads.some((h) => plain(h).includes(q));
    },
    submit() {
      this.$emit("submit", { dept: this.dept, user_ids: this.selected });
    },
  },
};
</script>

<style scoped>
.dh__now {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px 16px;
  border-radius: 14px;
  line-height: 1.45;
}
.dh__now p {
  margin: 0;
}
.dh__now-icon.v-icon {
  flex: none;
  align-self: flex-start;
  font-size: 28px;
}
.dh__now-title {
  font-weight: 700;
  overflow-wrap: anywhere;
}
.dh__now-text {
  margin-top: 2px !important;
  font-size: 0.95rem;
}
.dh__now--own {
  background: var(--mint-50);
  border: 1px solid var(--leaf-200);
  color: var(--ink);
}
.dh__now--own .dh__now-icon.v-icon {
  color: var(--green);
}
.dh__now--inherited {
  background: var(--sky-bg);
  border: 1px solid #c3d6ee;
  color: #173f6b;
}
.dh__now--inherited .dh__now-icon.v-icon {
  color: var(--sky);
}
.dh__now--none {
  background: var(--amber-bg);
  border: 1px solid #efd3a0;
  color: #5c3a07;
}
.dh__now--none .dh__now-icon.v-icon {
  color: var(--amber);
}
.dh__note {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  line-height: 1.4;
}
.dh__note + .dh__note {
  margin-top: 0;
}
.dh__note .v-icon {
  flex: none;
  margin-top: 1px;
  color: var(--ink-3);
}
</style>
